import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { createWedding, getWeddingById, updateWedding } from '../../firebase/firestore';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';

const WeddingForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { weddingId } = useParams();
  const { userData, currentUser, loading: authLoading } = useAuth();
  
  // Debug auth state
  useEffect(() => {
    console.log('Auth state in WeddingForm:', { userData, currentUser, authLoading });
  }, [userData, currentUser, authLoading]);
  
  const [wedding, setWedding] = useState({
    name: '',
    location: '',
    date: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isEditMode = !!weddingId;

  useEffect(() => {
    const fetchWedding = async () => {
      if (!isEditMode) return;
      
      setFetchLoading(true);
      try {
        const weddingData = await getWeddingById(weddingId);
        
        if (!weddingData) {
          setError(t('common.notFound'));
          return;
        }
        
        setWedding({
          name: weddingData.name || '',
          location: weddingData.location || '',
          date: weddingData.date ? weddingData.date.toDate() : new Date(),
        });
      } catch (err) {
        console.error('Error fetching wedding:', err);
        setError(t('common.error'));
      } finally {
        setFetchLoading(false);
      }
    };

    fetchWedding();
  }, [weddingId, isEditMode, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWedding(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setWedding(prev => ({ ...prev, date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      if (!userData) {
        throw new Error('User data not loaded');
      }

      // Get the user ID from userData
      const userId = userData.uid;
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      console.log('Wedding form data:', wedding);
      console.log('User data:', userData);

      // Validate required fields
      if (!wedding.name || !wedding.location || !wedding.date) {
        throw new Error('Please fill in all required fields');
      }

      // Prepare wedding data with proper date format for Firestore
      const weddingToSave = {
        ...wedding,
        // Ensure date is a proper JavaScript Date object
        date: wedding.date instanceof Date ? wedding.date : new Date(wedding.date)
      };

      if (isEditMode) {
        await updateWedding(weddingId, weddingToSave);
        setSuccess(t('weddings.updateSuccess'));
      } else {
        console.log('Creating new wedding with data:', weddingToSave);
        
        const result = await createWedding(weddingToSave, userId);
        console.log('Create wedding result:', result);
        
        if (!result || !result.success || !result.id) {
          throw new Error('Failed to create wedding: Invalid response');
        }
        
        setSuccess(t('weddings.createSuccess'));
        
        // Redirect to the wedding details page after a short delay
        setTimeout(() => {
          navigate(`/weddings/${result.id}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/weddings');
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1">
          {isEditMode ? t('weddings.edit') : t('weddings.create')}
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('weddings.name')}
                name="name"
                value={wedding.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('weddings.location')}
                name="location"
                value={wedding.location}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('weddings.date')}
                  value={wedding.date}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 2 }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : t('common.save')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default WeddingForm;
